/**
 * HelpOrientationCard.jsx — RC 1.63.1
 *
 * Lightweight, institution-toned landing stub that surfaces the
 * three things a freshly-authenticated user should always be able
 * to discover from their dashboard:
 *
 *   1. How It Works (workflow primer)
 *   2. The user guide for their role
 *   3. Workflow orientation (sub-bullet of the above)
 *
 * No marketing redesign. No onboarding wizard. No dismissible state.
 * This is a calm orientation rail — small, present, persistent.
 *
 * Props
 * ─────
 *   workspace : 'candidate' | 'employer'    (required)
 *   memberRole: 'owner'|'admin'|'recruiter'|'reviewer'|null
 *                                            (only relevant in employer)
 *
 * Visibility doctrine (employer):
 *   - "How it works" — always shown.
 *   - "Reviewer User Guide" — always shown (every employer role).
 *   - "Employer User Guide" — owner + admin only.
 *
 * Visibility doctrine (candidate):
 *   - "How it works" + "Candidate User Guide".
 */
import { Link } from 'react-router-dom';
import { BookOpen, Compass, ArrowUpRight } from 'lucide-react';

function GuideRow({ to, label, blurb, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="
        group flex items-start justify-between gap-3
        rounded-md border border-ndor-edge bg-ndor-surface-alt
        px-3 py-2.5 hover:border-ndor-gold/40 hover:bg-ndor-surface
        transition-colors
      "
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-ndor-gold" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-ndor-ink leading-tight">
            {label}
          </p>
          {blurb ? (
            <p className="text-xs text-ndor-ink-muted mt-0.5 leading-snug">
              {blurb}
            </p>
          ) : null}
        </div>
      </div>
      <ArrowUpRight className="h-3.5 w-3.5 mt-0.5 text-ndor-ink-muted group-hover:text-ndor-gold transition-colors shrink-0" />
    </Link>
  );
}

export default function HelpOrientationCard({ workspace, memberRole = null }) {
  const isCandidate = workspace === 'candidate';
  const isEmployer  = workspace === 'employer';
  const isOwnerOrAdmin = isEmployer && (memberRole === 'owner' || memberRole === 'admin');

  // How It Works URL is workspace-aware: candidates use the public
  // /how-it-works primer; employers use the dedicated workflow page
  // already shipped under the help namespace.
  const howItWorksHref = isCandidate
    ? '/how-it-works'
    : '/employer/help/workflow';

  return (
    <section
      className="rounded-lg border border-ndor-edge bg-ndor-surface p-4 sm:p-5"
      data-testid={`help-orientation-${workspace}`}
      aria-label="Help and orientation"
    >
      <header className="flex items-center gap-2 mb-3">
        <Compass className="h-4 w-4 text-ndor-gold" />
        <h2 className="text-sm font-semibold text-ndor-ink tracking-wide uppercase">
          Help &amp; orientation
        </h2>
      </header>

      <div className="grid gap-2 sm:grid-cols-2">
        <GuideRow
          to={howItWorksHref}
          label="How it works"
          blurb="A short walkthrough of the platform's workflow doctrine."
          testId={`help-orientation-${workspace}-how-it-works`}
        />

        {isCandidate ? (
          <GuideRow
            to="/help/candidate"
            label="Candidate User Guide"
            blurb="Profile, CV, Analyse Fit, application packs, signals, privacy."
            testId="help-orientation-candidate-guide"
          />
        ) : null}

        {isEmployer ? (
          <GuideRow
            to="/help/reviewer"
            label="Reviewer User Guide"
            blurb="Reviewer dossier, append-only notes, advance / hold / reject."
            testId="help-orientation-reviewer-guide"
          />
        ) : null}

        {isOwnerOrAdmin ? (
          <GuideRow
            to="/help/employer"
            label="Employer User Guide"
            blurb="Workspace setup, roles, lifecycle, entitlements, governance."
            testId="help-orientation-employer-guide"
          />
        ) : null}
      </div>
    </section>
  );
}
